import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Chip, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'

import { DEVICE_IMG, MedconnectDevice, MedconnectDevicesConfig, MedconnectDeviceValue } from '@/constants/medconnect-devices-types'
import { createDevice, deleteDevice, discoverBluetoothDevices, getConfig, getConnectorRelease, isNewerVersion, selectPlatformRelease, updateDevice } from '@/core/classes/MedconnectDevicesAPI'
import MedconnectDevices from '@/components/ui/cards/MedconnectDevicesCard'
import { MedconnectDeviceCardProps } from '@/components/ui/cards/MedconnectDevicesCard/MedconnectDeviceCard'
import { ConnectorBleDevice } from '@/constants/medconnect-devices-types'

import MedconnectDevicesForm from '../components/MedconnectDevicesForm'
import DeviceSetupWizard from '../components/device-setup-wizard/DeviceSetupWizard'
import { getDeviceLiveStatus, useConnectorGeneration, useConnectorHealth } from '../model/useConnectorHealth'
import { useDeviceProfiles } from '../model/useDeviceProfiles'

type SetupMode = 'simple' | 'advanced'

type ConnectorRelease = { currentVersion: string | null; latestVersion: string; downloadUrl?: string }

const HEALTH_POLL_MS = 3000

/**
 * Local devices settings. "Simple" walks the user through a guided setup;
 * "Advanced" keeps the raw form for technicians who know the fields.
 */
export default function MedconnectDevicesConfigurator() {
  const { t } = useTranslation()
  const [config, setConfig] = useState<MedconnectDevicesConfig | undefined>(undefined)
  const [mode, setMode] = useState<SetupMode>('simple')
  const [isWizardOpen, setIsWizardOpen] = useState(false)
  const [editingDevice, setEditingDevice] = useState<MedconnectDevice | undefined>(undefined)
  const [discoveredDevices, setDiscoveredDevices] = useState<ConnectorBleDevice[]>([])
  const [isDiscovering, setIsDiscovering] = useState(false)
  const [release, setRelease] = useState<ConnectorRelease | undefined>(undefined)
  const health = useConnectorHealth(HEALTH_POLL_MS)
  const connectorGeneration = useConnectorGeneration(health)
  const { profiles, isLoading: isLoadingProfiles } = useDeviceProfiles(connectorGeneration)
  // The guided setup needs the connector's profiles / discovery / health routes; older installs only have the raw form.
  const isLegacyConnector: boolean = health.isLegacy || (!!health.health && !isLoadingProfiles && profiles.length === 0)
  const canUseGuidedSetup: boolean = health.isSettled && !health.isUnreachable && !isLegacyConnector && !isLoadingProfiles
  const effectiveMode: SetupMode = isLegacyConnector ? 'advanced' : mode

  const refreshConfig = useCallback(async () => {
    const next = await getConfig()
    if (next?.INSTALLER_NAME) setConfig(next)
  }, [])

  const saveDevice = useCallback(
    async (device: MedconnectDeviceValue): Promise<boolean> => {
      if (editingDevice && !canUseGuidedSetup) return false
      const next = editingDevice ? await updateDevice(editingDevice._id, device) : await createDevice(device)
      if (!next?.Devices) return false
      setConfig(next)
      return true
    },
    [editingDevice, canUseGuidedSetup]
  )

  const handleAdvancedSubmit = async (values: MedconnectDeviceValue) => {
    const saved = await saveDevice(values)
    if (saved) setEditingDevice(undefined)
  }

  const handleDiscover = async () => {
    setIsDiscovering(true)
    try {
      const result = await discoverBluetoothDevices()
      setDiscoveredDevices(result.devices ?? [])
    } finally {
      setIsDiscovering(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('page.configuration.MedconnectDevice.confirmDelete'))) return
    const next = await deleteDevice(id)
    if (next?.Devices) setConfig(next)
  }

  const openWizard = (device?: MedconnectDevice) => {
    setEditingDevice(device)
    setIsWizardOpen(true)
  }

  const closeWizard = () => {
    setIsWizardOpen(false)
    setEditingDevice(undefined)
  }

  const deviceCards = useMemo((): MedconnectDeviceCardProps[] => {
    if (!config?.Devices) return []
    return config.Devices.map((device) => ({
      name: device.name,
      logoSrc: DEVICE_IMG[device.name],
      isLocal: true,
      liveStatus: getDeviceLiveStatus(health.health, { configId: device._id, configName: device.name }),
      editDevice: canUseGuidedSetup ? () => (effectiveMode === 'simple' ? openWizard(device) : setEditingDevice(device)) : undefined,
      deleteDevice: () => void handleDelete(device._id),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, health.health, effectiveMode, canUseGuidedSetup])

  useEffect(() => {
    void refreshConfig()
    getConnectorRelease()
      .then(({ currentVersion, manifest }) => {
        setRelease({ currentVersion, ...selectPlatformRelease(manifest, navigator.platform.toLowerCase().includes('mac')) })
      })
      .catch(() => setRelease(undefined))
    // reload after a (re)install too: the installed version changes while the page stays open
  }, [refreshConfig, connectorGeneration])

  if (health.isSettled && health.isUnreachable && !config) {
    return <Alert severity="info">{t('page.configuration.MedconnectDevice.noConnector')}</Alert>
  }

  // no installed version = a connector too old to report one, which is itself due for an update
  const hasUpdate = !!release && (release.currentVersion === null || isNewerVersion(release.latestVersion, release.currentVersion))

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h6">{t('page.configuration.MedconnectDevice.title')}</Typography>
          <ConnectorStatusChip health={{ ...health, isLegacy: isLegacyConnector }} />
        </Stack>
        <ToggleButtonGroup exclusive size="small" value={effectiveMode} onChange={(_event, value: SetupMode | null) => value && setMode(value)} sx={{ flexShrink: 0, '& .MuiToggleButton-root': { whiteSpace: 'nowrap', px: 2 } }}>
          <ToggleButton value="simple" disabled={!canUseGuidedSetup}>{t('page.configuration.MedconnectDevice.mode.simple')}</ToggleButton>
          <ToggleButton value="advanced">{t('page.configuration.MedconnectDevice.mode.advanced')}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {isLegacyConnector && (
        <Alert
          severity="warning"
          action={release?.downloadUrl ? <Button color="inherit" size="small" href={release.downloadUrl} target="_blank" rel="noreferrer">{t('common:button.download.tooltip')}</Button> : undefined}
        >
          {t('page.configuration.MedconnectDevice.outdated')}
        </Alert>
      )}

      {!isLegacyConnector && hasUpdate && release.downloadUrl && (
        <Alert severity="info" action={<Button color="inherit" size="small" href={release.downloadUrl} target="_blank" rel="noreferrer">{t('common:button.download.tooltip')}</Button>}>
          {t('page.configuration.MedconnectDevice.download')}
        </Alert>
      )}

      {effectiveMode === 'simple' ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Button variant="contained" startIcon={<AddIcon />} disabled={!canUseGuidedSetup} onClick={() => openWizard()} sx={{ whiteSpace: 'nowrap', flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}>
            {t('page.configuration.MedconnectDevice.addDevice')}
          </Button>
          <Typography variant="body2" color="text.secondary">{t('page.configuration.MedconnectDevice.simpleHint')}</Typography>
        </Stack>
      ) : (
        <Stack spacing={1}>
          {editingDevice && <Button variant="outlined" sx={{ alignSelf: 'flex-start' }} onClick={() => setEditingDevice(undefined)}>{t('common:button.cancel.label')}</Button>}
          <MedconnectDevicesForm
            onSubmit={handleAdvancedSubmit}
            isSubmitting={false}
            initialValues={editingDevice ? stripId(editingDevice) : undefined}
            discoveredDevices={discoveredDevices}
            onDiscover={canUseGuidedSetup ? handleDiscover : undefined}
            isDiscovering={isDiscovering}
          />
        </Stack>
      )}

      <MedconnectDevices devices={deviceCards} />

      {isWizardOpen && !isLegacyConnector && (
        <DeviceSetupWizard
          open
          onClose={closeWizard}
          initialDevice={editingDevice}
          profiles={profiles}
          isLoadingProfiles={isLoadingProfiles}
          health={health}
          release={release}
          onSave={saveDevice}
        />
      )}
    </Stack>
  )
}

function stripId(device: MedconnectDevice): MedconnectDeviceValue {
  const { _id, ...values } = device
  return values
}

function ConnectorStatusChip({ health }: { health: ReturnType<typeof useConnectorHealth> }) {
  const { t } = useTranslation()
  if (!health.isSettled) return null
  if (health.isLegacy) return <Chip size="small" color="warning" label={t('page.configuration.MedconnectDevice.connector.outdated')} />
  if (health.isUnreachable || !health.health) return <Chip size="small" color="error" label={t('page.configuration.MedconnectDevice.connector.unreachable')} />
  if (health.health.status === 'degraded') return <Chip size="small" color="warning" label={t('page.configuration.MedconnectDevice.connector.degraded')} />
  return <Chip size="small" color="success" label={t('page.configuration.MedconnectDevice.connector.reachable')} />
}
